import React, { useState, useEffect, useCallback } from 'react';
import { fetchTeknikServislerStok } from '../services/stokService';
import { fetchRandevuBedelleriHakedisizByTeknikServis } from '../services/randevuBedeliService';
import { parseApiDate } from '../utils/security';

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const formatRandevuTarihSaat = (randevuTarihi, randevuSaati) => {
  if (randevuTarihi == null && randevuSaati == null) return '—';
  const d = parseApiDate(randevuTarihi);
  const dateStr = d && !isNaN(d.getTime()) ? d.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '';
  const saat = randevuSaati != null && String(randevuSaati).trim() ? String(randevuSaati).trim() : '';
  if (!dateStr && !saat) return '—';
  if (dateStr && saat) return `${dateStr} ${saat}`;
  return dateStr || saat;
};

const DEBOUNCE_MS = 320;
const MIN_SEARCH_LENGTH = 3;

const HakedisOlusturModal = ({
  onClose,
  onSubmit,
  loading,
  initialTeknikServis = null,
  initialSelectedIds = [],
}) => {
  const isPrefilled = !!initialTeknikServis?.id;
  const [step, setStep] = useState(isPrefilled ? 2 : 1);
  const [techSearchQuery, setTechSearchQuery] = useState('');
  const [techSearchResults, setTechSearchResults] = useState([]);
  const [techSearchLoading, setTechSearchLoading] = useState(false);
  const [selectedTechService, setSelectedTechService] = useState(
    isPrefilled
      ? {
          id: String(initialTeknikServis.id),
          ad: initialTeknikServis.ad ?? '',
          kod: initialTeknikServis.kod ?? '',
        }
      : null
  );

  const [list, setList] = useState([]);
  const [selectedIds, setSelectedIds] = useState(
    isPrefilled ? (initialSelectedIds || []).map(String) : []
  );
  const [listLoading, setListLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Adım 1: Teknik servis arama (debounce). En az 3 karakter ile arama yapılır.
  useEffect(() => {
    if (step !== 1) return;
    const q = (techSearchQuery || '').trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setTechSearchResults([]);
      setTechSearchLoading(false);
      return;
    }
    const t = setTimeout(() => {
      setTechSearchLoading(true);
      fetchTeknikServislerStok({ arama: q, limit: 30 })
        .then((res) => {
          if (res?.basarili && res?.veri?.teknikServisler) {
            setTechSearchResults(res.veri.teknikServisler.map((ts) => ({
              id: ts.id?.toString?.() || ts.id,
              ad: ts.ad ?? '',
              kod: ts.kod ?? '',
            })));
          } else {
            setTechSearchResults([]);
          }
        })
        .catch(() => setTechSearchResults([]))
        .finally(() => setTechSearchLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [step, techSearchQuery]);

  const handleSelectTechService = useCallback((ts) => {
    setSelectedTechService(ts);
    setStep(2);
    setList([]);
    setSelectedIds([]);
    setLoadError('');
  }, []);

  // Adım 2: Seçilen teknik servise ait hakedişe eklenmemiş bedelleri yükle
  useEffect(() => {
    if (step !== 2 || !selectedTechService?.id) return;
    setListLoading(true);
    setLoadError('');
    fetchRandevuBedelleriHakedisizByTeknikServis(selectedTechService.id)
      .then((res) => {
        if (res?.basarili && res?.veri?.randevuBedelleri) {
          setList(res.veri.randevuBedelleri);
          if (isPrefilled && initialSelectedIds?.length) {
            const ids = initialSelectedIds.map(String);
            setSelectedIds(ids);
          } else {
            setSelectedIds([]);
          }
        } else {
          setList([]);
        }
      })
      .catch(() => {
        setLoadError('Liste yüklenemedi');
        setList([]);
      })
      .finally(() => setListLoading(false));
  }, [step, selectedTechService?.id, isPrefilled, initialSelectedIds]);

  const toggle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toplam = list
    .filter((rb) => selectedIds.includes(String(rb.id || rb._id)))
    .reduce((s, rb) => s + (parseFloat(rb.toplamTutar) || 0), 0);

  const handleSubmit = () => {
    onSubmit(selectedIds);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedTechService(null);
    setTechSearchQuery('');
    setTechSearchResults([]);
    setList([]);
    setSelectedIds([]);
    setLoadError('');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '720px', width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Hakediş oluştur {step === 2 && selectedTechService ? `— ${selectedTechService.ad || selectedTechService.kod}` : ''}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {step === 1 && (
            <>
              <p style={{ marginBottom: '12px', fontSize: '13px', color: 'var(--text-gray)' }}>
                Önce teknik servis seçin. Arama en az {MIN_SEARCH_LENGTH} karakter ile yapılır.
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>Teknik servis ara (ad veya kod)</label>
                <input
                  type="text"
                  value={techSearchQuery}
                  onChange={(e) => setTechSearchQuery(e.target.value)}
                  placeholder={`En az ${MIN_SEARCH_LENGTH} karakter yazın...`}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid var(--border-color)', borderRadius: '6px', boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>
              {techSearchLoading && <div style={{ padding: '12px', color: 'var(--text-gray)' }}>Aranıyor...</div>}
              {!techSearchLoading && (
                <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                  {techSearchResults.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-gray)' }}>
                      {techSearchQuery.trim().length < MIN_SEARCH_LENGTH
                        ? `En az ${MIN_SEARCH_LENGTH} karakter yazın`
                        : 'Sonuç yok'}
                    </div>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {techSearchResults.map((ts) => (
                        <li key={ts.id}>
                          <button
                            type="button"
                            onClick={() => handleSelectTechService(ts)}
                            style={{
                              width: '100%',
                              padding: '12px 14px',
                              textAlign: 'left',
                              border: 'none',
                              borderBottom: '1px solid var(--border-color)',
                              background: 'var(--bg-light, #f8fafc)',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = 'var(--primary-light, #e0e7ff)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.background = 'var(--bg-light, #f8fafc)'; }}
                          >
                            <strong>{ts.ad || '—'}</strong>
                            {ts.kod != null && ts.kod !== '' && <span style={{ marginLeft: '8px', color: 'var(--text-gray)' }}>({ts.kod})</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              {!isPrefilled && (
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button type="button" className="secondary-btn" onClick={handleBack} style={{ padding: '6px 10px', fontSize: '13px' }}>
                    <i className="fas fa-arrow-left"></i> Teknik servis değiştir
                  </button>
                </div>
              )}
              <p style={{ marginBottom: '12px', fontSize: '13px', color: 'var(--text-gray)' }}>
                Bu teknik servise ait hakedişe eklenmemiş (onaylı) randevu bedellerini seçin.
              </p>
              {listLoading ? (
                <div style={{ padding: '24px', textAlign: 'center' }}>Yükleniyor...</div>
              ) : loadError ? (
                <div style={{ color: 'var(--error)', padding: '16px' }}>{loadError}</div>
              ) : list.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-gray)' }}>
                  Bu teknik servise ait hakedişe eklenmemiş randevu bedeli yok
                </div>
              ) : (
                <div className="hakedis-olustur-table-wrap" style={{ width: '100%', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto' }}>
                  <table className="admins-table" style={{ width: '100%', minWidth: 0, tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 44, minWidth: 44 }}></th>
                        <th style={{ width: 130, minWidth: 100 }}>Randevu No</th>
                        <th style={{ width: 130, minWidth: 110 }}>Tarih / Saat</th>
                        <th style={{ width: 100, minWidth: 90, textAlign: 'right' }}>Bedel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((rb) => {
                        const id = String(rb.id || rb._id);
                        const checked = selectedIds.includes(id);
                        return (
                          <tr key={id}>
                            <td style={{ width: 44, minWidth: 44, verticalAlign: 'middle' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(id)}
                                aria-label={`Seç: ${rb.randevuNumarasi}`}
                              />
                            </td>
                            <td style={{ width: 130, minWidth: 100, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{rb.randevuNumarasi || '—'}</td>
                            <td style={{ width: 130, minWidth: 110, fontSize: '13px', whiteSpace: 'nowrap' }}>{formatRandevuTarihSaat(rb.randevuTarihi, rb.randevuSaati)}</td>
                            <td style={{ width: 100, minWidth: 90, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrency(rb.toplamTutar)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {selectedIds.length > 0 && (
                <p style={{ marginTop: '12px', fontWeight: 600 }}>
                  Seçilen: {selectedIds.length} bedel — Toplam: {formatCurrency(toplam)}
                </p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '16px' }}>
          <div>
            {step === 2 && !isPrefilled && (
              <button type="button" className="secondary-btn" onClick={handleBack}>
                Geri
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            <button type="button" className="secondary-btn" onClick={onClose}>
              İptal
            </button>
            {step === 2 && (
              <button
                type="button"
                className="primary-btn"
                onClick={handleSubmit}
                disabled={loading || selectedIds.length === 0}
              >
                {loading ? 'Oluşturuluyor...' : 'Hakediş oluştur'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HakedisOlusturModal;
